import * as yup from "yup";

const ssRefNumRegex = /^[A-Z0-9-)(/]{1,200}$/;
const vehicleRegNumRegex = /^[A-Z0-9-](?:[A-Z0-9 -]{1,30})?$/;
const trailerRegNumRegex = /^[A-Z0-9-](?:[A-Z0-9 -]{1,30})?$/;

// Helper to prevent empty strings from triggering regex errors
const nullableString = (regex, message) =>
  yup
    .string()
    .nullable()
    .transform((value) => (value === "" ? null : value))
    .test("regex-test", message, (value) => {
      if (!value) return true;
      return regex.test(value);
    });

const hasValue = (value) => {
  if (value == null) return false;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

export const NewGvmsDeclarationValidator = yup.object().shape({
  organization: yup
    .object()
    .shape({
      id: yup.string().required("Client is required"),
    })
    .required("Client is required"),
  transport: yup.object().shape({
    vehicleRegNum: yup
      .string()
      .required("Vehicle registration number is required.")
      .matches(vehicleRegNumRegex, "Invalid vehicle registration format"),
    isUnaccompanied: yup.boolean(),
    trailerRegNum: yup.array().of(
      yup
        .string()
        .transform((value) => (value === "" ? undefined : value))
        .matches(trailerRegNumRegex, "Invalid trailer format"),
    ),
    containerRefNum: yup
      .array()
      .of(
        yup.string().transform((value) => (value === "" ? undefined : value)),
      ),
    ssMrns: yup.array().of(yup.string()),
    departurePort: yup.string().required("Departure port is required"),
    arrivalPort: yup.string().required("Arrival port is required"),
    carrierCompany: yup.string().required("Carrier is required"),
    crossingID: yup.string().nullable(),
    crossingDepartureDateTime: yup
      .string()
      .nullable()
      .required("Departure date is required"),
    clientId: yup.string().nullable(),
    correlationId: yup.string().nullable(),
    organizationId: yup.string().nullable(),
  }),

  contents: yup
    .object()
    .shape({
      exemptionDec: yup
        .object()
        .nullable()
        .shape({
          ssRefNum: yup
            .array()
            .of(
              yup
                .string()
                .matches(ssRefNumRegex, "Invalid Exemption S&S Ref Num"),
            )
            .min(1, "At least one Exemption reference is required"),
        }),

      emptyVehicle: yup
        .object()
        .nullable()
        .shape({
          isOwned: yup.boolean(),
          ssRefNum: nullableString(
            ssRefNumRegex,
            "Invalid S&S MRN for empty vehicle",
          ),
        }),

      customsDec: yup.array().of(
        yup.object().shape({
          ssRefNum: yup
            .string()
            .nullable()
            .transform((value) => (value === "" ? null : value))
            .matches(ssRefNumRegex, "Invalid S&S Ref Num"),
        }),
      ),

      transitDec: yup.array().of(
        yup.object().shape({
          mrn: yup.string().required("Transit Declaration ID required"),
          isTSAD: yup.boolean(),
          ssRefNum: yup
            .string()
            .nullable()
            .transform((value) => (value === "" ? null : value))
            .matches(ssRefNumRegex, "Invalid S&S Ref Num"),
        }),
      ),

      tirDec: yup.array().of(
        yup.object().shape({
          carnetId: yup.string().required("TIR Carnet ID required"),
          ssRefNum: yup
            .string()
            .required("S&S Ref Num required")
            .matches(ssRefNumRegex, "Invalid S&S Ref Num"),
        }),
      ),

      ataDec: yup.array().of(
        yup.object().shape({
          carnetId: yup.string().required("ATA Carnet ID required"),
          ssRefNum: yup
            .string()
            .nullable()
            .transform((value) => (value === "" ? null : value))
            .matches(ssRefNumRegex, "Invalid S&S Ref Num"),
        }),
      ),

      dbcDec: yup
        .object()
        .nullable()
        .shape({
          isOwned: yup.boolean(),
          ssRefNum: yup
            .string()
            .required("S&S Ref Num required")
            .matches(ssRefNumRegex, "Invalid S&S Ref Num"),
        }),

      eidrDec: yup.array().of(
        yup.object().shape({
          tin: yup.string().required("TIN required"),
          ssRefNum: yup
            .string()
            .required("S&S Ref Num required")
            .matches(ssRefNumRegex, "Invalid S&S Ref Num"),
        }),
      ),

      mtpDec: yup
        .object()
        .nullable()
        .shape({
          ssRefNum: yup
            .array()
            .of(
              yup
                .string()
                .required("S&S Ref Num required")
                .matches(ssRefNumRegex, "Invalid S&S Ref Num"),
            )
            .min(1, "At least one S&S Ref Num required"),
        }),
    })
    .test(
      "contents-required-when-no-empty-vehicle",
      "If empty vehicle is not provided, at least one declaration is required.",
      function (contents) {
        if (!contents) return false;

        if (contents.emptyVehicle != null) {
          return true;
        }

        const hasOtherDeclaration =
          hasValue(contents.exemptionDec) ||
          hasValue(contents.customsDec) ||
          hasValue(contents.transitDec) ||
          hasValue(contents.tirDec) ||
          hasValue(contents.ataDec) ||
          hasValue(contents.dbcDec) ||
          hasValue(contents.eidrDec) ||
          hasValue(contents.mtpDec);

        return hasOtherDeclaration;
      },
    ),
});
